import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '../../../hooks/useToast'
import { useUploadLimits } from '../../../hooks/useUploadLimits'
import {
  bytesToMegabytes,
  checkImageDimensions,
  type ImageDimensionRules,
  ImageUploadError,
  type ImageUploadTarget,
  imageTypeLabels,
  imageUrl,
  readImageSize,
  uploadImage,
} from '../../../lib/uploads'
import { Button, ErrorMessage, Label, Skeleton } from '../../atoms'

interface ImageUploadFieldProps {
  id: string
  label: string
  target: ImageUploadTarget
  applicationId: string
  value: string
  onChange: (path: string) => void
  onUploadingChange?: (uploading: boolean) => void
  // Optional pixel rules (proportions and/or min/max size), checked before the upload
  dimensions?: ImageDimensionRules
  skeleton?: boolean
  // A form-level error for this field, e.g. "Image is required"
  error?: string
}

export const ImageUploadField = memo(
  ({
    id,
    label,
    target,
    applicationId,
    value,
    onChange,
    onUploadingChange,
    dimensions,
    skeleton,
    error,
  }: ImageUploadFieldProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const limits = useUploadLimits()
    const [uploading, setUploading] = useState(false)
    const hintId = `${id}-hint`

    const types = limits ? imageTypeLabels(limits.acceptedTypes) : ''
    const sizeMb = limits ? bytesToMegabytes(limits.maxBytes) : null

    const hint = useMemo(() => {
      const parts: string[] = []
      if (limits) {
        parts.push(t('common.upload.hint', { types, size: sizeMb }))
      }
      if (dimensions?.aspectRatio) {
        parts.push(t('common.upload.hintRatio', dimensions.aspectRatio))
      }
      if (dimensions?.minWidth || dimensions?.minHeight) {
        parts.push(
          t('common.upload.hintMinSize', {
            width: dimensions.minWidth ?? 0,
            height: dimensions.minHeight ?? 0,
          }),
        )
      }
      if (dimensions?.maxWidth || dimensions?.maxHeight) {
        parts.push(
          t('common.upload.hintMaxSize', {
            width: dimensions.maxWidth ?? '∞',
            height: dimensions.maxHeight ?? '∞',
          }),
        )
      }
      return parts.join(' ')
    }, [dimensions, limits, sizeMb, t, types])

    const errorMessage = useCallback(
      (code: ImageUploadError['code']) => {
        switch (code) {
          case 'type':
            return types
              ? t('common.upload.errors.type', { types })
              : t('common.upload.errors.typeGeneric')
          case 'size':
            return sizeMb
              ? t('common.upload.errors.size', { size: sizeMb })
              : t('common.upload.errors.sizeGeneric')
          case 'pixels':
            return limits?.maxPixels
              ? t('common.upload.errors.pixels', {
                  megapixels: Math.floor(limits.maxPixels / 1_000_000),
                })
              : t('common.upload.errors.pixelsGeneric')
          case 'ratio':
            return t('common.upload.errors.ratio', dimensions?.aspectRatio)
          case 'tooSmall':
            return t('common.upload.errors.tooSmall', {
              width: dimensions?.minWidth ?? 0,
              height: dimensions?.minHeight ?? 0,
            })
          case 'tooLarge':
            return t('common.upload.errors.tooLarge', {
              width: dimensions?.maxWidth ?? '∞',
              height: dimensions?.maxHeight ?? '∞',
            })
          default:
            return t(`common.upload.errors.${code}`)
        }
      },
      [dimensions, limits?.maxPixels, sizeMb, t, types],
    )

    const setBusy = useCallback(
      (busy: boolean) => {
        setUploading(busy)
        onUploadingChange?.(busy)
      },
      [onUploadingChange],
    )

    const handleFileChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target
        const file = input.files?.[0]
        if (!file) {
          return
        }
        setBusy(true)
        try {
          if (dimensions) {
            const problem = checkImageDimensions(await readImageSize(file), dimensions)
            if (problem) {
              throw new ImageUploadError(problem)
            }
          }
          onChange(await uploadImage(file, target, applicationId))
        } catch (err) {
          showToast(errorMessage(err instanceof ImageUploadError ? err.code : 'server'), 'error')
        } finally {
          setBusy(false)
          input.value = ''
        }
      },
      [applicationId, dimensions, errorMessage, onChange, setBusy, showToast, target],
    )

    const handleRemove = useCallback(() => onChange(''), [onChange])

    if (skeleton) {
      return (
        <div className="form-group image-upload">
          <span className="form-label">{label}</span>
          <Skeleton shape="upload" />
          <Skeleton shape="text" />
        </div>
      )
    }

    return (
      <div className="form-group image-upload">
        <Label htmlFor={id}>{label}</Label>
        {value ? (
          <div className="image-upload__preview">
            <img
              className="image-upload__image"
              src={imageUrl(value)}
              alt={t('common.upload.previewAlt', { label })}
              width={120}
              height={120}
            />
            <Button type="button" variant="secondary" onClick={handleRemove} disabled={uploading}>
              {t('common.actions.removeImage')}
            </Button>
          </div>
        ) : null}
        <input
          id={id}
          type="file"
          accept={limits ? limits.acceptedTypes.join(',') : 'image/*'}
          className="image-upload__input"
          onChange={handleFileChange}
          disabled={uploading}
          aria-describedby={hintId}
        />
        <span id={hintId} className="form-hint" aria-live="polite">
          {uploading ? t('common.upload.uploading') : hint}
        </span>
        <ErrorMessage message={error} />
      </div>
    )
  },
)

ImageUploadField.displayName = 'ImageUploadField'
